import React, { useState, useEffect, useMemo } from 'react';
import { Table, Typography, Spin, Alert } from 'antd';
import type { TableProps } from 'antd';
import dayjs from 'dayjs';
import { getRequest } from '../../../api/httpRequest';

const { Text } = Typography;

interface DetailData {
  order: string;
  division: string;
  position: string;
  note: string;
}

interface TableData {
  key: number;
  num: number;
  dateBegin: string | null;
  dateEnd: string | null;
  sum: string;
  type: string;
  name: string;
  details: DetailData;
}

const ExpandedRow: React.FC<{ data: DetailData }> = ({ data }) => (
  <div style={{ padding: '16px 24px', background: '#fafafa' }}>
    <div style={{ display: 'grid', gridTemplateColumns: '200px 1fr', gap: '12px' }}>
      <Text strong>Приказ:</Text>
      <Text>{data.order}</Text>

      <Text strong>Подразделение:</Text>
      <Text>{data.division}</Text>

      <Text strong>Должность:</Text>
      <Text>{data.position}</Text>

      <Text strong>Описание:</Text>
      <Text style={{ whiteSpace: 'pre-wrap' }}>{data.note}</Text>
    </div>
  </div>
);

const PaymentOrdersTab: React.FC = () => {
  const [data, setData] = useState<TableData[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [expandedRowKeys, setExpandedRowKeys] = useState<number[]>([]);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    getRequest("/server/proxy/zik/GetStim")
      .then(resp => {
        const formattedData: TableData[] = resp.data.map((item: any, index: number) => ({
          key: index,
          num: item.no,
          dateBegin: item.dateBegin ? dayjs(item.dateBegin).format('DD.MM.YYYY') : null,
          dateEnd: item.dateEnd ? dayjs(item.dateEnd).format('DD.MM.YYYY') : null,
          sum: item.sum,
          type: item.type,
          name: item.name,
          details: {
            order: item.order,
            division: item.division,
            position: item.position,
            note: item.note
          }
        }));

        setData(formattedData);
        setError(null);
        setLoading(false);
      })
      .catch(error => {
        setError(error instanceof Error ? error.message : 'Unknown error');
        setLoading(false);
      });
  };

  const handleRowClick = (record: TableData) => {
    const key = record.key;
    setExpandedRowKeys(prev =>
      prev.includes(key) ? prev.filter(k => k !== key) : [...prev, key]
    );
  };

  // Вычисление уникальных годов для фильтрации
  const yearFilters = useMemo(() => {
    const years = new Set<number>();
    const currentYear = dayjs().year();
    data.forEach(item => {
      if (item.dateBegin) {
        const parts = item.dateBegin.split('.');
        if (parts.length === 3) {
          const year = parseInt(parts[2], 10);
          if (!isNaN(year) && year <= currentYear) years.add(year);
        }
      }
    });
    return Array.from(years)
      .sort((a, b) => b - a)
      .map(year => ({ text: year.toString(), value: year }));
  }, [data]);

  const columns: TableProps<TableData>['columns'] = [
    {
      title: '№',
      dataIndex: 'num',
      key: 'num',
      width: 80,
      align: 'center',
      sorter: (a, b) => a.num - b.num
    },
    {
      title: 'Начало',
      dataIndex: 'dateBegin',
      key: 'dateBegin',
      width: 120,
      align: 'center',
      render: (value) => value || '-',
      filters: yearFilters,
      onFilter: (value, record) => {
        if (!record.dateBegin) return false;
        const parts = record.dateBegin.split('.');
        if (parts.length !== 3) return false;
        const year = parseInt(parts[2], 10);
        return year === value;
      }
    },
    {
      title: 'Окончание',
      dataIndex: 'dateEnd',
      key: 'dateEnd',
      width: 120,
      align: 'center',
      render: (value) => value || '-'
    },
    {
      title: 'Размер',
      dataIndex: 'sum',
      key: 'sum',
      width: 150,
      align: 'right',
      sorter: (a, b) => {
        const numA = parseFloat(a.sum.replace(/\s/g, ''));
        const numB = parseFloat(b.sum.replace(/\s/g, ''));
        return numA - numB;
      }
    },
    {
      title: 'Тип',
      dataIndex: 'type',
      key: 'type',
      width: 150
    },
    {
      title: 'Наименование',
      dataIndex: 'name',
      key: 'name',
      ellipsis: true
    }
  ];

  if (error) {
    return (
      <Alert
        message="Ошибка загрузки данных"
        description={error}
        type="error"
        showIcon
        action={
          <button
            onClick={fetchData}
            style={{ marginTop: '8px', padding: '8px 16px', cursor: 'pointer' }}
          >
            Повторить попытку
          </button>
        }
      />
    );
  }

  return (
    <Spin spinning={loading} tip="Загрузка данных...">
      <Table<TableData>
        columns={columns}
        dataSource={data}
        size="middle"
        pagination={{
          pageSize: 10,
          showSizeChanger: true,
          showQuickJumper: true,
          showTotal: (total) => `Всего записей: ${total}`
        }}
        expandable={{
          expandedRowRender: (record) => <ExpandedRow data={record.details} />,
          expandedRowKeys,
          onExpand: (expanded, record) => handleRowClick(record),
          rowExpandable: () => true
        }}
        onRow={(record) => ({
          onClick: () => handleRowClick(record)
        })}
        rowClassName="cursor-pointer"
        scroll={{ x: 'max-content' }}
      />
    </Spin>
  );
};

export default PaymentOrdersTab;